import { memo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useDataMutation } from 'hooks'
import { useFirebaseConnect } from 'react-redux-firebase'
import { useLocation, useRouteMatch } from 'react-router-dom'

import { paths } from 'app/paths'
import { isLocalhost, logToAnalytics, openLink } from 'modules'

import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    makeStyles,
    Typography
} from '@material-ui/core'
import LimitationImage from 'assets/images/basic-limitation.png'

const useStyles = makeStyles(({ spacing, fontWeightBold }) => ({
    title: {
        fontSize: '16px',
        textAlign: 'center',
        fontWeight: fontWeightBold
    },
    actionsWrapper: {
        justifyContent: 'center',
        paddingBottom: spacing(3)
    }
}))

const routesToHide = [paths.editQuickGuidde, paths.editPlaybook, paths.domainRedirect]

export const SessionExpiredDialog = memo(() => {
    const classes = useStyles()
    const location = useLocation()
    const ignoreThisRoute = useRouteMatch(routesToHide)?.isExact ?? false

    useFirebaseConnect({
        path: `/desiredDomain/domain`,
        storeAs: 'desiredDomain'
    })

    const desiredDomain = useSelector(
        state => state.firebase.data.desiredDomain as unknown as string | null
    )
    const {
        serviceUsage: {
            dialogs: { updatePlanDialog, upgradePlanDialog }
        },
        spaceDialog
    } = useSelector(state => state)

    // on localhost we'll always get expired session because it's not guidde.com domain
    // so we say that localhost is always the correct domain
    const correctDomain = !desiredDomain || desiredDomain === window.location.host || isLocalhost

    const {
        data,
        isLoading,
        mutate: getSignInCode
    } = useDataMutation<{}, { code: string }, Error>('/c/v1/generate-one-time-code', 'POST')

    useEffect(() => {
        // make code request only once if domain is not correct
        if (correctDomain || isLoading || data?.code) return

        getSignInCode({})
    }, [correctDomain, data?.code, getSignInCode, isLoading])

    const ignoreDialog =
        updatePlanDialog || upgradePlanDialog || spaceDialog.isOpened || ignoreThisRoute

    if (ignoreDialog || correctDomain || isLoading || !data?.code) return null

    const queryParams = new URLSearchParams({
        code: data.code,
        ...(location.pathname && { fromPathname: location.pathname }),
        ...(location.search && { fromSearch: location.search })
    }).toString()
    const redirectLink = `https://${desiredDomain}/domain-redirect?${queryParams}`

    return (
        <>
            <Dialog fullWidth maxWidth="sm" open>
                <DialogContent>
                    <Box textAlign="center">
                        <img src={LimitationImage} />
                    </Box>
                    <Typography className={classes.title}>Your session has expired</Typography>
                    <Box textAlign="center">Please click login to continue.</Box>
                </DialogContent>

                <DialogActions className={classes.actionsWrapper}>
                    <Button
                        size="medium"
                        type="button"
                        onClick={() => {
                            logToAnalytics('refresh_domain_clicked')
                            openLink(redirectLink, '_self')
                        }}
                    >
                        Login
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
})
